import get from 'lodash/get';
import InviteParentText from './InviteParent';
import PromotionCta from '../../../components/UI/PromotionCta/index.tsx';
import { CreditApplicationStatus } from '../../../services/credit/index.ts';
import { getPlanNameText } from '../CreditApplication/helpers/index.tsx';
import { useToggles } from '../../../state/toggles';
import { useUser } from '../../../state/user/index.ts';
import { planPrices } from '../../../utils/prices';

const YOUR_INFO_HEADER = () => "Let's get to know you.";
const YOUR_INFO_CREDIT_HEADER = () => 'Create your Greenlight account.';
const YOUR_CHILD_INFO_HEADER = () => "Let's set up your child's account.";
const YOUR_CHILD_INFO_SUBHEADER = () =>
  'You can upgrade to a custom debit card later.';
const YOUR_CHILD_INFO_IMAGE_BLACK_CARD = () => 'custom-black-card-fanned.png';
const PROVE_PREFILL_HEADER = () => 'What is your date of birth?';
const VERIFY_IDENTITY_HEADER = () => "Let's verify your identity.";
const VERIFY_ADDRESS_HEADER = () => "What's your address?";
const SET_UP_WALLET_HEADER = app => {
  const { parentFundWalletDesign } = app;
  if (!parentFundWalletDesign?.enabled) {
    return 'Let’s set up your Parent’s Wallet';
  }

  if (parentFundWalletDesign.variationKey === 'variation_1') {
    return 'Load funds to send to your child';
  }
  return 'Set up funds for your child';
};
const SET_UP_WALLET_SUBHEADER = app => {
  const { parentFundWalletDesign } = app;
  if (!parentFundWalletDesign?.enabled) {
    return 'Your Greenlight Wallet works like a real wallet. Load it with funds and make transfers to your child whenever, wherever, and for whatever.';
  }

  if (parentFundWalletDesign?.variationKey === 'variation_1') {
    return 'Load your Greenlight Wallet with funds and make transfers to your child whenever, wherever, and for whatever.';
  }
  return 'Load your Greenlight Wallet with funds and make transfers to your child whenever, wherever, and for whatever.';
};
const SET_UP_WALLET_TEXT = app => (
  <h3>
    {app.walletMinimum?.displayAmount === 0.0
      ? 'Choose an initial amount to load.'
      : `Choose an initial amount of $${
          app.walletMinimum?.displayAmount || app.walletMinimum?.amount
        } or more to load.`}
  </h3>
);
const ADD_FUNDING_SOURCE_HEADER_INLINE = () => (
  <h3>How will you fund your Greenlight wallet?</h3>
);
const ADD_FUNDING_SOURCE_HEADER = () => 'Add funding source.';
const ADD_FUNDING_SOURCE_SUBHEADER = app => {
  const toggles = useToggles();
  const plans = planPrices(toggles);

  const planName = get(app, 'answers.setPlan.pricingPlanName') || 'standard';
  const currentPlan = (app.allPricingPlans || []).find(
    plan => plan.name === planName
  );
  const currentFee = currentPlan ? currentPlan.fee : plans.standard;

  const fundingAmount =
    app.answers && app.answers.setInitialFundingAmount
      ? parseFloat(app.answers.setInitialFundingAmount).toFixed(2)
      : '';

  const creditRejectedOrDeclined =
    app.answers.creditApplication?.creditDecision ===
      CreditApplicationStatus.REJECTED ||
    (app.answers.creditApplication &&
      !app.answers.creditApplication.creditAccepted);

  const creditDelayedOrApproved =
    app.answers.creditApplication?.creditDecision ===
      CreditApplicationStatus.DELAYED ||
    app.answers.creditApplication?.creditDecision ===
      CreditApplicationStatus.APPROVED;

  if (!fundingAmount) {
    if (creditDelayedOrApproved) {
      return (
        <h2>
          Your payment account pays your credit card bill and{' '}
          {getPlanNameText(planName)} monthly subscription fee.
          <br />
          <br />
          After your 1-month free trial, your subscription is ${plans[planName]}
          /month.
        </h2>
      );
    }
    return <h2>This is how you will fund your Greenlight Wallet.</h2>;
  }

  if (creditRejectedOrDeclined) {
    return (
      <h2>
        This is how you'll fund your Greenlight Wallet with ${fundingAmount}. We
        will also fund or connect it to other Greenlight products that you may
        add or participate in at a later date.
        <br />
        <br />
        After your 1-month free trial, your subscription is ${plans[planName]}
        /month.
      </h2>
    );
  }

  return (
    <>
      <h2>
        This is how you will fund your Greenlight Wallet with ${fundingAmount}.
      </h2>
      {app.promotionId && (
        <PromotionCta
          promotionId={app.promotionId}
          ctaType="fundingAccount"
          planName={planName}
          currentFee={currentFee}
        />
      )}
    </>
  );
};

const CHILD_INFO_HEADER = () => {
  const { kidFirstRegParentInviteV1 } = useToggles();
  if (
    kidFirstRegParentInviteV1?.enabled &&
    kidFirstRegParentInviteV1?.variables?.variant === 'sms'
  ) {
    return 'Your info';
  }
  return 'Set up your account';
};
const CHILD_INFO_SUBHEADER = () => {
  const { kidFirstRegParentInviteV1 } = useToggles();
  if (
    kidFirstRegParentInviteV1.enabled &&
    kidFirstRegParentInviteV1?.variables?.variant === 'sms'
  ) {
    return (
      <div>
        <img
          src="../../static/child-piggybank-default.svg"
          style={{
            display: 'inline-block',
            verticalAlign: 'middle',
            paddingRight: '10px'
          }}
        />
        Child's info
      </div>
    );
  }
  return null;
};

const INVITE_PARENT_HEADER = () => {
  const { userIsTeen, referralRewardValue } = useUser();
  const baseCopy = 'Send a request to your parent';
  const { growthRegistrationFlowFields } = useToggles();
  if (!referralRewardValue || !userIsTeen) {
    return baseCopy;
  }
  return (
    growthRegistrationFlowFields?.variables?.parent_screen_head || baseCopy
  );
};
const INVITE_PARENT_SUBHEADER = () => {
  const { userIsTeen, referralRewardValue } = useUser();
  const { growthRegistrationFlowFields, kidFirstRegParentInviteV0 } =
    useToggles();
  const baseCopy = kidFirstRegParentInviteV0.enabled
    ? 'We already emailed your parent or guardian to get you set up. Send an extra nudge by entering their phone number.'
    : "Enter your Greenlight (or guardian's) mobile number so they can get the app and order your Greenlight card.";
  if (!userIsTeen || !referralRewardValue) {
    return baseCopy;
  }
  return (
    growthRegistrationFlowFields?.variables?.parent_screen_subhead || baseCopy
  );
};

const CREDIT_FUNDING_HEADER = () => 'Link your payment account';

const strings = {
  parent_flow_1: {
    group_1: {
      header: YOUR_INFO_HEADER
    },
    group_2: {
      header: YOUR_INFO_HEADER
    },
    group_3: {
      header: YOUR_CHILD_INFO_HEADER,
      image: YOUR_CHILD_INFO_IMAGE_BLACK_CARD
    },
    group_4: {
      header: VERIFY_IDENTITY_HEADER
    },
    group_5: {
      header: SET_UP_WALLET_HEADER,
      subheader: SET_UP_WALLET_SUBHEADER,
      text: SET_UP_WALLET_TEXT
    },
    group_6: {
      header: ADD_FUNDING_SOURCE_HEADER,
      subheader: ADD_FUNDING_SOURCE_SUBHEADER
    },
    group_7: {}
  },
  parent_flow_5: {
    group_1: {
      header: YOUR_INFO_HEADER
    },
    group_2: {
      header: YOUR_CHILD_INFO_HEADER,
      image: YOUR_CHILD_INFO_IMAGE_BLACK_CARD
    },
    group_3: {
      header: VERIFY_IDENTITY_HEADER
    },
    group_4: {
      header: SET_UP_WALLET_HEADER,
      subheader: SET_UP_WALLET_SUBHEADER,
      text: SET_UP_WALLET_TEXT,
      widgets: [{}, { text: ADD_FUNDING_SOURCE_HEADER_INLINE }]
    },
    group_5: {}
  },
  approver_flow_2: {
    group_1: {
      header: YOUR_INFO_HEADER
    },
    group_2: {
      header: YOUR_INFO_HEADER
    }
  },
  parent_flow_6: {
    group_1: {
      header: YOUR_INFO_HEADER
    },
    group_2: {
      header: YOUR_CHILD_INFO_HEADER,
      subheader: YOUR_CHILD_INFO_SUBHEADER,
      image: YOUR_CHILD_INFO_IMAGE_BLACK_CARD
    },
    group_3: {
      header: VERIFY_IDENTITY_HEADER
    },
    group_4: {
      header: SET_UP_WALLET_HEADER,
      subheader: SET_UP_WALLET_SUBHEADER,
      text: SET_UP_WALLET_TEXT
    },
    group_5: {
      header: ADD_FUNDING_SOURCE_HEADER,
      subheader: ADD_FUNDING_SOURCE_SUBHEADER
    },
    group_6: {}
  },
  parent_flow_7: {
    group_1: {
      header: YOUR_INFO_HEADER
    },
    group_2: {
      header: SET_UP_WALLET_HEADER,
      subheader: SET_UP_WALLET_SUBHEADER,
      text: SET_UP_WALLET_TEXT
    },
    group_3: {
      header: ADD_FUNDING_SOURCE_HEADER,
      subheader: ADD_FUNDING_SOURCE_SUBHEADER
    },
    group_4: {
      header: VERIFY_IDENTITY_HEADER
    },
    group_5: {
      header: YOUR_CHILD_INFO_HEADER,
      subheader: YOUR_CHILD_INFO_SUBHEADER,
      image: YOUR_CHILD_INFO_IMAGE_BLACK_CARD
    },
    group_6: {}
  },
  parent_flow_8: {
    group_1: {
      header: YOUR_INFO_HEADER
    },
    group_2: {
      header: YOUR_CHILD_INFO_HEADER,
      subheader: YOUR_CHILD_INFO_SUBHEADER,
      image: YOUR_CHILD_INFO_IMAGE_BLACK_CARD
    },
    group_3: {
      header: VERIFY_ADDRESS_HEADER
    },
    group_4: {
      header: VERIFY_IDENTITY_HEADER
    },
    group_5: {
      header: SET_UP_WALLET_HEADER,
      subheader: SET_UP_WALLET_SUBHEADER,
      text: SET_UP_WALLET_TEXT
    },
    group_6: {
      header: ADD_FUNDING_SOURCE_HEADER,
      subheader: ADD_FUNDING_SOURCE_SUBHEADER
    },
    group_7: {}
  },
  parent_flow_9: {
    group_1: {
      header: YOUR_INFO_HEADER
    },
    group_2: {
      header: YOUR_CHILD_INFO_HEADER,
      subheader: YOUR_CHILD_INFO_SUBHEADER,
      image: YOUR_CHILD_INFO_IMAGE_BLACK_CARD
    },
    group_3: {
      header: VERIFY_IDENTITY_HEADER
    },
    group_4: {
      header: SET_UP_WALLET_HEADER,
      subheader: SET_UP_WALLET_SUBHEADER,
      text: SET_UP_WALLET_TEXT
    },
    group_5: {
      header: ADD_FUNDING_SOURCE_HEADER,
      subheader: ADD_FUNDING_SOURCE_SUBHEADER
    },
    group_6: {}
  },
  parent_flow_10: {
    group_1: {
      header: YOUR_INFO_HEADER
    },
    group_2: {},
    group_3: {
      header: YOUR_CHILD_INFO_HEADER,
      subheader: YOUR_CHILD_INFO_SUBHEADER,
      image: YOUR_CHILD_INFO_IMAGE_BLACK_CARD
    },
    group_4: {
      header: VERIFY_IDENTITY_HEADER
    },
    group_5: {
      header: SET_UP_WALLET_HEADER,
      subheader: SET_UP_WALLET_SUBHEADER,
      text: SET_UP_WALLET_TEXT
    },
    group_6: {
      header: ADD_FUNDING_SOURCE_HEADER,
      subheader: ADD_FUNDING_SOURCE_SUBHEADER
    },
    group_7: {}
  },
  parent_flow_11: {
    group_1: {
      header: YOUR_INFO_HEADER
    },
    group_2: {},
    group_3: {
      header: YOUR_CHILD_INFO_HEADER,
      subheader: YOUR_CHILD_INFO_SUBHEADER,
      image: YOUR_CHILD_INFO_IMAGE_BLACK_CARD
    },
    group_4: {
      header: PROVE_PREFILL_HEADER
    },
    group_5: {},
    group_6: {
      header: VERIFY_IDENTITY_HEADER
    },
    group_7: {
      header: SET_UP_WALLET_HEADER,
      subheader: SET_UP_WALLET_SUBHEADER,
      text: SET_UP_WALLET_TEXT
    },
    group_8: {
      header: ADD_FUNDING_SOURCE_HEADER,
      subheader: ADD_FUNDING_SOURCE_SUBHEADER
    },
    group_9: {}
  },
  parent_flow_12: {
    group_1: {
      header: YOUR_INFO_HEADER
    },
    group_2: {
      header: YOUR_CHILD_INFO_HEADER,
      subheader: YOUR_CHILD_INFO_SUBHEADER,
      image: YOUR_CHILD_INFO_IMAGE_BLACK_CARD
    },
    group_3: {},
    group_4: {
      header: VERIFY_IDENTITY_HEADER
    },
    group_5: {
      header: SET_UP_WALLET_HEADER,
      subheader: SET_UP_WALLET_SUBHEADER,
      text: SET_UP_WALLET_TEXT
    },
    group_6: {
      header: ADD_FUNDING_SOURCE_HEADER,
      subheader: ADD_FUNDING_SOURCE_SUBHEADER
    },
    group_7: {}
  },
  parent_flow_13: {
    group_1: {
      header: YOUR_INFO_HEADER
    },
    group_2: {},
    group_3: {
      header: YOUR_CHILD_INFO_HEADER,
      subheader: YOUR_CHILD_INFO_SUBHEADER,
      image: YOUR_CHILD_INFO_IMAGE_BLACK_CARD
    },
    group_4: {},
    group_5: {
      header: VERIFY_IDENTITY_HEADER
    },
    group_6: {
      header: SET_UP_WALLET_HEADER,
      subheader: SET_UP_WALLET_SUBHEADER,
      text: SET_UP_WALLET_TEXT
    },
    group_7: {
      header: ADD_FUNDING_SOURCE_HEADER,
      subheader: ADD_FUNDING_SOURCE_SUBHEADER
    },
    group_8: {}
  },
  parent_flow_14: {
    group_1: {
      header: YOUR_INFO_HEADER
    },
    group_2: {
      header: YOUR_CHILD_INFO_HEADER,
      subheader: YOUR_CHILD_INFO_SUBHEADER,
      image: YOUR_CHILD_INFO_IMAGE_BLACK_CARD
    },
    group_3: {},
    group_4: {
      header: VERIFY_IDENTITY_HEADER
    },
    group_5: {
      header: SET_UP_WALLET_HEADER,
      subheader: SET_UP_WALLET_SUBHEADER,
      text: SET_UP_WALLET_TEXT
    },
    group_6: {
      header: ADD_FUNDING_SOURCE_HEADER,
      subheader: ADD_FUNDING_SOURCE_SUBHEADER
    },
    group_7: {}
  },
  parent_flow_15: {
    group_1: {
      header: YOUR_INFO_HEADER
    },
    group_2: {},
    group_3: {
      header: YOUR_CHILD_INFO_HEADER,
      subheader: YOUR_CHILD_INFO_SUBHEADER,
      image: YOUR_CHILD_INFO_IMAGE_BLACK_CARD
    },
    group_4: {
      header: PROVE_PREFILL_HEADER
    },
    group_5: {},
    group_6: {
      header: VERIFY_IDENTITY_HEADER
    },
    group_7: {
      header: SET_UP_WALLET_HEADER,
      subheader: SET_UP_WALLET_SUBHEADER,
      text: SET_UP_WALLET_TEXT
    },
    group_8: {
      header: ADD_FUNDING_SOURCE_HEADER,
      subheader: ADD_FUNDING_SOURCE_SUBHEADER
    },
    group_9: {}
  },
  parent_flow_16: {
    group_1: {
      header: YOUR_INFO_CREDIT_HEADER
    },
    group_2: {},
    group_3: {
      header: PROVE_PREFILL_HEADER
    },
    group_4: {},
    group_5: {
      header: VERIFY_IDENTITY_HEADER
    },
    group_6: {},
    group_7: {
      header: CREDIT_FUNDING_HEADER,
      subheader: ADD_FUNDING_SOURCE_SUBHEADER
    },
    group_8: {},
    fund_wallet: {
      header: SET_UP_WALLET_HEADER,
      subheader: SET_UP_WALLET_SUBHEADER,
      text: SET_UP_WALLET_TEXT
    }
  },
  parent_flow_17: {
    group_1: {
      header: YOUR_INFO_CREDIT_HEADER
    },
    group_2: {},
    group_3: {
      header: YOUR_CHILD_INFO_HEADER,
      subheader: YOUR_CHILD_INFO_SUBHEADER,
      image: YOUR_CHILD_INFO_IMAGE_BLACK_CARD
    },
    group_4: {
      header: PROVE_PREFILL_HEADER
    },
    group_5: {},
    group_6: {
      header: VERIFY_IDENTITY_HEADER
    },
    group_7: {},
    group_8: {
      header: CREDIT_FUNDING_HEADER,
      subheader: ADD_FUNDING_SOURCE_SUBHEADER
    },
    group_9: {},
    fund_wallet: {
      header: SET_UP_WALLET_HEADER,
      subheader: SET_UP_WALLET_SUBHEADER,
      text: SET_UP_WALLET_TEXT
    }
  },
  child_flow_3: {
    group_1: {
      header: CHILD_INFO_HEADER,
      subheader: CHILD_INFO_SUBHEADER
    },
    group_2: {
      header: INVITE_PARENT_HEADER,
      subheader: INVITE_PARENT_SUBHEADER,
      widgets: [
        {},
        {
          text: () => <InviteParentText />
        }
      ]
    }
  }
};

export default strings;
